import React, { FC, useState } from 'react'
import 'antd/dist/reset.css'
import cx from 'classnames'
import { Tabs } from 'antd'
import { v4 as uuidv4 } from 'uuid'

// components
// eslint-disable-next-line import/no-cycle
import TabsElementsBuilder from './TabsElementsBuilder'

// assets
import { ReactComponent as CloseIcon } from '../../../assets/icons/BaseIcons/x-icon.svg'
import { ReactComponent as PlusIcon } from '../../../assets/icons/BaseIcons/plus-1-icon.svg'
import { ReactComponent as TabsIcon } from '../../../assets/icons/ElementIcons/section-icon.svg'
import { ReactComponent as EditIcon } from '../../../assets/icons/BaseMiniIcons/pencil-icon.svg'

// types
import { IGridItem, ITabBoxLayoutRef } from '../../../types/interfaces'
import { PAGE } from '../../../utils/enums'

const { TabPane } = Tabs

type Props = {
	formId?: string
	onAddNewTab: Function
	onRemoveTab: Function
	onClickSwitch: Function
	tabs: any
	itemKey: string
	selectedItem: string
	selectedNestedTabItem: string
	openItem: Function
	onLayoutChange: any
	onNewItemAdded: Function
	itemY: number
	itemX: number
	layoutHeight: number
	reload: Function
	resizeElement: Function
	mainContainerWidth: number
	selectedTabItem: string
	openItemInTabsElement: Function
	onLayoutChangeInTabsElement: Function
	onNewItemAddedToTabsElement: Function
	tabBoxLayoutReferences: ITabBoxLayoutRef
	nestedElementsRendered: Function
	onDragStartInTab: Function
	isDroppable: boolean
	locked: boolean
	tabBoxParenKey?: string
	tabBoxParentTabKey?: string
	onRender?: Function
	page?: PAGE
}

const TabElement: FC<Props> = (props) => {
	const {
		tabs,
		itemKey,
		onAddNewTab,
		onRemoveTab,
		onClickSwitch,
		itemX,
		itemY,
		selectedItem,
		openItem,
		onLayoutChange,
		onNewItemAdded,
		layoutHeight,
		onRender,
		reload,
		formId,
		resizeElement,
		page,
		mainContainerWidth,
		selectedTabItem,
		openItemInTabsElement,
		onLayoutChangeInTabsElement,
		onNewItemAddedToTabsElement,
		tabBoxLayoutReferences,
		tabBoxParenKey,
		tabBoxParentTabKey,
		nestedElementsRendered,
		onDragStartInTab,
		isDroppable,
		selectedNestedTabItem,
		locked
	} = props
	const [activeKey, setActiveKey] = useState<string>(props?.tabs[0]?.key)

	const onChange = async (key: string) => {
		await setActiveKey(key)
		// call onWindowResize when user change tab
		window.dispatchEvent(new Event('resize'))
	}

	// add new tab to tabs and update active tab key
	const add = () => {
		const key = uuidv4()
		// for nested tab
		onAddNewTab(itemKey, key, tabBoxParenKey, tabBoxParentTabKey)
		setActiveKey(key)
	}

	// deleting of tab from tabs and updating active tab key
	const remove = (targetKey: string) => {
		const panes = tabs
		let newActiveKey = activeKey
		let lastIndex = 0
		panes.forEach((pane: any, i: number) => {
			if (pane.key === targetKey) {
				lastIndex = i - 1
			}
		})
		onRemoveTab(itemKey, targetKey, tabBoxParenKey, tabBoxParentTabKey)
		const newPanes = panes.filter((pane: any) => pane.key !== targetKey)
		if (newPanes.length && newActiveKey === targetKey) {
			if (lastIndex >= 0) {
				newActiveKey = newPanes[lastIndex].key
			} else {
				newActiveKey = newPanes[0].key
			}
		}
		setActiveKey(newActiveKey)
	}

	// this function handle event from tabs ant component for add or remove tab
	const onEdit = (event: any, action: string) => {
		if (action === 'add') {
			add()
		} else if (action === 'remove') {
			remove(event)
		}
	}

	// locking of tabs element
	const onLocked = () => {
		const status = !locked
		onClickSwitch(itemKey, status, tabBoxParenKey, tabBoxParentTabKey)
	}

	const editClass = cx({
		'locked-element': locked
	})

	const lockedClass = cx({
		'not-locked': !locked
	})

	return (
		<div className={'flex-1 overflow-auto'}>
			<div className={'tab-element-right-box'}>
				<span id={'tab-box-edit-icon'} className={editClass} onClick={onLocked}>
					<EditIcon width={14} height={14} />
				</span>
				<span id={'tab-box-icon'}>
					<TabsIcon width={20} height={20} />
				</span>
			</div>
			<Tabs type='editable-card' onChange={onChange} activeKey={activeKey} onEdit={onEdit} addIcon={<PlusIcon />} hideAdd={false} className={lockedClass}>
				{tabs.map((pane: any) => (
					<TabPane tab={pane.title} key={pane.key} closable={pane.closable} closeIcon={<CloseIcon width={20} height={20} />}>
						<TabsElementsBuilder
							itemX={itemX}
							itemY={itemY}
							itemKey={itemKey}
							tabKey={pane.key}
							layouts={pane?.data?.layout}
							items={pane?.data?.items}
							selectedItem={selectedItem}
							selectedNestedTabItem={selectedNestedTabItem}
							openItem={(gridItem: IGridItem) => {
								openItem(gridItem, itemKey, activeKey, tabBoxParenKey, tabBoxParentTabKey)
							}}
							onLayoutChange={(layout: any) => onLayoutChange(itemKey, activeKey, layout, tabBoxParenKey, tabBoxParentTabKey)}
							onNewItemAdded={(droppedItemData: any, layoutItem: any) =>
								onNewItemAdded(droppedItemData, layoutItem, itemKey, activeKey, tabBoxParenKey, tabBoxParentTabKey)
							}
							layoutHeight={layoutHeight}
							onRender={onRender}
							reload={reload}
							formId={formId}
							onClickSwitch={onClickSwitch}
							onAddNewTab={onAddNewTab}
							onRemoveTab={onRemoveTab}
							selectedTabItem={selectedTabItem}
							openItemInTabsElement={openItemInTabsElement}
							onLayoutChangeInTabsElement={onLayoutChangeInTabsElement}
							onNewItemAddedToTabsElement={onNewItemAddedToTabsElement}
							tabBoxLayoutReferences={tabBoxLayoutReferences?.[`${itemKey}`]?.[`${pane.key}`]}
							// get parentKey and parentTabKey from child component (nested tab box)
							resizeElement={(layout: IGridItem[], item: IGridItem, parentKey: string, parentTabKey: string) => {
								resizeElement(layout, item, itemKey, activeKey, parentKey, parentTabKey)
							}}
							page={page}
							mainContainerWidth={mainContainerWidth}
							nestedElementsRendered={nestedElementsRendered}
							onDragStartInTab={onDragStartInTab}
							isDroppable={isDroppable}
						/>
					</TabPane>
				))}
			</Tabs>
		</div>
	)
}

export default React.memo(TabElement)
